import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { readMailState, writeMailState } from "@/lib/mailUrlState";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, X } from "lucide-react";
import { mailApi } from "@/api/mailApi";
import { driveApi } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { keys, messageStreamKey } from "@/api/hooks";
import { FolderPane } from "./FolderPane";
import { ResizableListColumn } from "./ResizableListColumn";
import { useAppTitle } from "@/lib/useAppTitle";
import { cn } from "@/lib/utils";
import { MessageList, type InboxTab } from "./MessageList";
import { MailCommandBar, displayFolderName, type CommandBarActions } from "./MailCommandBar";
import { isUpdate } from "./categoryTheme";
import { usePreferences } from "@/lib/preferences";
import { useMailCommands } from "@/lib/mailCommands";
import { ReadingPane } from "./ReadingPane";
import { emptyFilter, matchesFilter, type MessageFilterState } from "./MessageFilters";
import { useMailShortcuts, type GoToFolder } from "./useMailShortcuts";
import { ShortcutsDialog } from "./ShortcutsDialog";
import { ConversationView } from "./ConversationView";
import { SearchChips } from "./SearchChips";
import { groupConversations, type Conversation } from "./threading";
import { useConversationView, useShortcutsEnabled } from "@/lib/mailViewPrefs";
import { highlightTerms } from "@/lib/searchQuery";
import {
    useApplyLabel,
    useDeleteDraft,
    useDrafts,
    useFolders,
    useLabels,
    useSnoozeMessage,
    useIdentities,
    useMessage,
    useMessageAction,
    useMessages,
    MESSAGE_PAGE_SIZE,
    useSaveDraft,
    useScheduleSend,
    useSearch,
    useSendMessage,
    useThreadKeys
} from "@/api/hooks";
import { useStatusAnnouncer } from "@/components/StatusAnnouncer";
import type { ComposePayload } from "./ComposePane";
import type { DraftDto, Message, MessageSummary } from "@/api/mailApi";
import {
    seedForDraft,
    seedForForward,
    seedForNew,
    seedForReply,
    type ComposeSeed
} from "@/lib/composeDraft";
import { pushDesktopNotification, setDesktopUnreadBadge } from "@/lib/useDesktopShell";
import { loadNotifPrefs } from "@/lib/notifications";
import { useCreateTaskFromMessage, useTaskTargets } from "@/features/tasks/fromMessage";

const ComposePane = lazy(() =>
    import("./ComposePane").then((m) => ({ default: m.ComposePane }))
);

/** Search results per page — "1–50 of N". */
const SEARCH_PAGE_SIZE = 50;

interface UndoEntry {
    label: string;
    run: () => void;
    /** Show the "… Undo" toast (moves); flag/read changes are undoable via z only. */
    toast: boolean;
    at: number;
}

interface ShellContext {
    searchInputRef?: React.RefObject<HTMLInputElement>;
}

interface ComposeState {
    seed: ComposeSeed;
    draftId: string | null;
    /** Stable key for this compose session — never changes after open, so
     *  React does NOT remount ComposePane when auto-save assigns a draft ID. */
    key: string;
    /** When true (opened via a message's "AI reply" button), the compose pane
     *  auto-runs the AI reply generator and previews it. */
    autoAiReply?: boolean;
}

export function MailPage() {
    const context = useOutletContext<ShellContext | undefined>();
    const [searchParams, setSearchParams] = useSearchParams();
    // Folder, message, page and search live in the address as one opaque
    // token (?s=…, lib/mailUrlState) so a refresh — or a shared link — reopens
    // the same place, without the ids being readable. Old plain links
    // (?folder&m&page&q) still open and are rewritten to the token.
    const urlState = readMailState(searchParams);
    const searchQuery = urlState.q ?? "";
    const searchMode = searchQuery.trim().length > 0;

    const [selectedFolderId, setSelectedFolderId] = useState(() => urlState.folder ?? "inbox");
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(() => urlState.m ?? null);
    // Folder lists scroll-load (infinite scroll), so the address no longer carries a page.
    useEffect(() => {
        setSearchParams((prev) => writeMailState(prev, {
            ...readMailState(prev),
            folder: selectedFolderId,
            m: selectedMessageId ?? undefined,
            page: undefined
        }), { replace: true });
    }, [selectedFolderId, selectedMessageId, setSearchParams]);
    // Layout preference (Settings → General, Ctrl+K), saved per user — lib/preferences.ts.
    const readingPane = usePreferences((p) => p.layout.readingPane);
    const [compose, setCompose] = useState<ComposeState | null>(null);
    // With the pane hidden the list is the screen; opening a message swaps
    // to the reader (master/detail) rather than showing nothing at all.
    const readerVisible = Boolean(selectedMessageId) || Boolean(compose);
    const [filters, setFilters] = useState<MessageFilterState>(emptyFilter);
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    const [lastDraftSavedAt, setLastDraftSavedAt] = useState<Date | null>(null);
    const [draftSaveError, setDraftSaveError] = useState<string | null>(null);

    // Captures the draft id as soon as auto-save completes. Needed because
    // handleSend reads compose?.draftId from the closure, but if the user
    // clicks Send before React flushes the auto-save onSuccess state update,
    // compose.draftId may still be null — leaving the draft behind.
    const lastSavedDraftIdRef = useRef<string | null>(null);

    const isDraftsFolder = !searchMode && selectedFolderId === "drafts";

    const folders = useFolders();
    const messages = useMessages(selectedFolderId);
    const drafts = useDrafts();
    // Search paging lives beside the query; a new query starts on page 1.
    const [searchPage, setSearchPage] = useState(1);
    useEffect(() => setSearchPage(1), [searchQuery]);
    const search = useSearch(
        { query: searchQuery, skip: (searchPage - 1) * SEARCH_PAGE_SIZE, take: SEARCH_PAGE_SIZE },
        searchMode
    );
    const setSearchQuery = useCallback((q: string) => {
        setSearchParams((prev) => writeMailState(prev, { ...readMailState(prev), q: q.trim() || undefined }));
    }, [setSearchParams]);
    const identities = useIdentities();
    const send = useSendMessage();
    const saveDraft = useSaveDraft();
    const deleteDraft = useDeleteDraft();
    const scheduleSend = useScheduleSend();
    const announce = useStatusAnnouncer().announce;

    // "inbox" is the default id and an alias the BFF accepts, but IMAP names the
    // folder "INBOX" — resolve it so the title, the folder-pane highlight, the
    // unread count and the Direct / Updates tabs find the folder. The message
    // query keeps the original id (no refetch).
    const inboxFolder = folders.data?.find((f) => f.kind === "inbox");
    const shownFolderId = selectedFolderId === "inbox" && inboxFolder ? inboxFolder.id : selectedFolderId;
    const currentFolder = folders.data?.find((f) => f.id === shownFolderId);
    const folderName = searchMode
        ? "Search results"
        : currentFolder ? displayFolderName(currentFolder) : "Mail";

    // Inbox unread count in the browser-tab title (Gmail / Outlook
    // pattern). Document-title side-effect runs only on Inbox change so
    // we don't thrash every keystroke. Clear on unmount so navigating
    // out of Mail (e.g. into Settings) restores the default title.
    const inboxUnread = folders.data?.find((f) => f.kind === "inbox")?.unreadCount ?? 0;
    const previousInboxUnread = useRef<number | null>(null);
    const [newMailToast, setNewMailToast] = useState<{ count: number } | null>(null);
    const newMailToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // "(3) Athavita Mail - Hemant Kumar" — Outlook's "Mail - Name" tab title.
    const titleBase = useAppTitle("Athavita Mail");
    useEffect(() => {
        document.title = inboxUnread > 0 ? `(${inboxUnread}) ${titleBase}` : titleBase;
        return () => { document.title = titleBase; };
    }, [inboxUnread, titleBase]);
    useEffect(() => {
        setDesktopUnreadBadge(inboxUnread);

        const previous = previousInboxUnread.current;
        if (previous !== null && inboxUnread > previous) {
            const arrived = inboxUnread - previous;
            // In-app bottom-left toast (shows even when window is focused)
            if (newMailToastTimerRef.current) clearTimeout(newMailToastTimerRef.current);
            setNewMailToast({ count: arrived });
            newMailToastTimerRef.current = setTimeout(() => setNewMailToast(null), 6000);
            // Native OS notification only when window is blurred, and only if the user hasn't
            // turned new-mail alerts off in Settings → Notifications (those preferences were
            // previously saved but never read by anything).
            const notifPrefs = loadNotifPrefs();
            if (!document.hasFocus() && notifPrefs.newMail) {
                pushDesktopNotification(
                    arrived === 1 ? "New mail — Athavita" : `${arrived} new mails — Athavita`,
                    "Click to open your inbox.",
                    { sound: notifPrefs.sound }
                );
            }
        }
        previousInboxUnread.current = inboxUnread;
    }, [inboxUnread]);

    // Drafts folder reads from /api/drafts and presents them as message
    // summaries. Clicking one re-opens compose with the draft's fields.
    const draftMessages = useMemo<MessageSummary[]>(
        () => (drafts.data ?? []).map(draftToSummary),
        [drafts.data]
    );

    // Every loaded batch as one list. New mail shifts the server's offsets, so a
    // later batch can repeat the end of the one before — keep each message once.
    const streamMessages = useMemo<MessageSummary[]>(() => {
        const seen = new Set<string>();
        const out: MessageSummary[] = [];
        for (const p of messages.data?.pages ?? []) {
            for (const m of p.items) {
                if (seen.has(m.id)) continue;
                seen.add(m.id);
                out.push(m);
            }
        }
        return out;
    }, [messages.data]);
    const streamTotal = messages.data?.pages[0]?.total ?? 0;

    const activeMessages = searchMode
        ? search.data?.messages ?? []
        : isDraftsFolder
            ? draftMessages
            : streamMessages;
    const activePending = searchMode
        ? search.isPending
        : isDraftsFolder
            ? drafts.isPending
            : messages.isPending;
    // A failed background poll (a transient 503 while the mailbox is briefly
    // unreachable) keeps the last good list on screen — the error only shows
    // when there is nothing to show.
    const activeError = searchMode
        ? search.isError && !search.data
        : isDraftsFolder
            ? drafts.isError && !drafts.data
            : messages.isError && !messages.data;
    const activeErrorMessage = searchMode
        ? search.error?.message
        : isDraftsFolder
            ? drafts.error?.message
            : messages.error?.message;

    const messageDetail = useMessage(
        compose || isDraftsFolder ? null : selectedMessageId
    );

    // Direct / Updates tabs on the inbox (rule-based categories, no AI —
    // Doc/MAIL_UI_REDESIGN.md). Like conversation grouping, the split is over
    // the page that was fetched; the counts are its unread messages.
    const inboxTabsPref = usePreferences((p) => p.list.inboxTabs);
    const tabsOn = inboxTabsPref && !searchMode && currentFolder?.kind === "inbox";
    const [inboxTab, setInboxTab] = useState<InboxTab>("direct");
    const tabUnread = useMemo(() => {
        const counts: Record<InboxTab, number> = { direct: 0, updates: 0 };
        for (const m of activeMessages) if (!m.isRead) counts[isUpdate(m.category) ? "updates" : "direct"] += 1;
        return counts;
    }, [activeMessages]);

    // Rows opened while a filter is on stay listed until the filter, tab or
    // folder changes (Outlook). Without this, opening a message under
    // "Unread" marked it read, dropped it from the list, auto-opened the next
    // one, and so on — every unread message was marked read in a few seconds.
    const [keptIds, setKeptIds] = useState<ReadonlySet<string>>(() => new Set());
    useEffect(() => { setKeptIds(new Set()); }, [filters, inboxTab, selectedFolderId, searchMode]);
    useEffect(() => {
        if (!selectedMessageId) return;
        const row = activeMessages.find((m) => m.id === selectedMessageId);
        // Only a row the filter showed when it was opened is kept.
        if (!row || !matchesFilter(row, filters)) return;
        setKeptIds((prev) => (prev.has(selectedMessageId) ? prev : new Set(prev).add(selectedMessageId)));
    }, [selectedMessageId, activeMessages, filters]);
    const filteredMessages = useMemo(
        () => activeMessages.filter((m) =>
            (matchesFilter(m, filters) || keptIds.has(m.id))
            && (!tabsOn || (inboxTab === "updates") === isUpdate(m.category))),
        [activeMessages, filters, tabsOn, inboxTab, keptIds]
    );

    // ── Conversation view ────────────────────────────────────────────────
    // Groups the rows of the fetched page by thread (header chain from the
    // BFF when the inbox is IMAP, else normalised subject within 30 days).
    // Paging stays per message, as the server counts.
    const [conversationView, setConversationView] = useConversationView();
    const threadingOn = conversationView && !isDraftsFolder;
    const threadKeys = useThreadKeys(filteredMessages.map((m) => m.id), threadingOn);
    const conversations = useMemo<Conversation[] | null>(
        () => (threadingOn ? groupConversations(filteredMessages, threadKeys.data) : null),
        [threadingOn, filteredMessages, threadKeys.data]
    );
    const conversationOf = useMemo(() => {
        const map = new Map<string, Conversation>();
        for (const c of conversations ?? []) for (const m of c.messages) map.set(m.id, c);
        return map;
    }, [conversations]);
    const conversationByRow = useMemo(
        () => new Map((conversations ?? []).map((c) => [c.latest.id, c] as const)),
        [conversations]
    );
    const listMessages = useMemo(
        () => (conversations ? conversations.map((c) => c.latest) : filteredMessages),
        [conversations, filteredMessages]
    );
    const selectedConversation = selectedMessageId ? conversationOf.get(selectedMessageId) ?? null : null;
    /** The list row standing for the open message (its conversation's row). */
    const activeRowId = selectedConversation?.latest.id ?? selectedMessageId;
    /** Every message a row stands for — the whole conversation in conversation view. */
    const expandIds = useCallback(
        (ids: string[]) => ids.flatMap((id) => conversationByRow.get(id)?.messages.map((m) => m.id) ?? [id]),
        [conversationByRow]
    );
    const summaryById = useMemo(() => new Map(activeMessages.map((m) => [m.id, m] as const)), [activeMessages]);

    useEffect(() => {
        // Wait for the list: while it loads it is empty, and clearing the
        // selection then would throw away the message restored from the
        // address on refresh.
        if (compose || activePending) return;
        if (listMessages.length === 0) {
            setSelectedMessageId(null);
            return;
        }
        if (!selectedMessageId || !filteredMessages.some((m) => m.id === selectedMessageId)) {
            setSelectedMessageId(listMessages[0]!.id);
        }
    }, [listMessages, filteredMessages, selectedMessageId, compose, activePending]);

    const handleSelectFolder = (id: string) => {
        setSelectedFolderId(id);
        setSelectedMessageId(null);
        setInboxTab("direct");
        setFilters(emptyFilter());
        setCheckedIds(new Set());
        setCompose(null);
        setLastDraftSavedAt(null);
        setDraftSaveError(null);
    };

    // Bulk actions — the bar shows up when any row is checked.
    const messageAction = useMessageAction();

    // ── Undo (z) ─────────────────────────────────────────────────────────
    // Moves record where each message went (IMAP MOVE gives it a new id)
    // and move it back; flag / read changes record their inverse.
    const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
    // Right-click › Create task (To Do lists).
    const taskTargets = useTaskTargets();
    const createTaskFrom = useCreateTaskFromMessage();
    const moveWithUndo = useCallback((ids: string[], target: "archive" | "trash" | "junk") => {
        if (ids.length === 0) return;
        const origins = ids.map((id) => ({ id, back: summaryById.get(id)?.folderId ?? selectedFolderId }));
        const n = ids.length;
        const label = target === "archive"
            ? (n > 1 ? `${n} messages archived` : "Archived")
            : target === "trash"
                ? (n > 1 ? `${n} messages moved to Trash` : "Moved to Trash")
                : (n > 1 ? `${n} messages moved to Junk` : "Moved to Junk");
        announce(label);
        void Promise.all(origins.map(async (o) => {
            try {
                const res = target === "junk"
                    ? await messageAction.mutateAsync({ type: "move", id: o.id, folderId: "junk" })
                    : await messageAction.mutateAsync({ type: target, id: o.id });
                return res && res.id ? { newId: res.id, back: o.back } : null;
            } catch {
                return null; // the hook already rolled the list back
            }
        })).then((results) => {
            const moves = results.filter((r): r is { newId: string; back: string } => r !== null);
            if (moves.length === 0) return;
            setUndoEntry({
                label,
                toast: true,
                at: Date.now(),
                run: () => {
                    for (const m of moves) messageAction.mutate({ type: "move", id: m.newId, folderId: m.back });
                }
            });
        });
    }, [summaryById, selectedFolderId, messageAction, announce]);

    const setFlagsWithUndo = useCallback((ids: string[], kind: "read" | "unread" | "flag" | "unflag") => {
        if (ids.length === 0) return;
        for (const id of ids) messageAction.mutate({ type: kind, id });
        const inverse = kind === "read" ? "unread" : kind === "unread" ? "read" : kind === "flag" ? "unflag" : "flag";
        const label = kind === "read" ? "Marked as read" : kind === "unread" ? "Marked as unread" : kind === "flag" ? "Starred" : "Star removed";
        announce(label);
        setUndoEntry({
            label,
            toast: false,
            at: Date.now(),
            run: () => {
                for (const id of ids) messageAction.mutate({ type: inverse, id });
            }
        });
    }, [messageAction, announce]);

    const handleUndo = useCallback(() => {
        if (!undoEntry) {
            announce("Nothing to undo");
            return;
        }
        undoEntry.run();
        setUndoEntry(null);
        announce("Action undone");
    }, [undoEntry, announce]);

    const handleToggleChecked = useCallback((id: string) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const handleToggleCheckedAll = useCallback((next: boolean) => {
        setCheckedIds(next ? new Set(listMessages.map((m) => m.id)) : new Set());
    }, [listMessages]);
    // Hover-prefetch: warm the message-detail cache before the user
    // clicks. TanStack auto-skips if the row is already cached + fresh
    // (staleTime 30s), so spammy mouseover doesn't re-fetch.
    const qc = useQueryClient();
    const prefetchToken = useAccessToken();
    const handlePrefetchMessage = useCallback((id: string) => {
        qc.prefetchQuery({
            queryKey: keys.message(id, prefetchToken),
            queryFn: () => mailApi.getMessage(id, prefetchToken),
            staleTime: 30_000
        });
    }, [qc, prefetchToken]);

    // Warm the next two rows once a message is open, so j/k, Delete and
    // Archive (which open the next row) show it without a loading state.
    useEffect(() => {
        if (!selectedMessageId || !messageDetail.data || isDraftsFolder) return;
        const i = listMessages.findIndex((m) => m.id === selectedMessageId);
        if (i < 0) return;
        for (const m of [listMessages[i + 1], listMessages[i + 2], listMessages[i - 1]]) {
            if (m) handlePrefetchMessage(m.id);
        }
    }, [selectedMessageId, messageDetail.data, listMessages, isDraftsFolder, handlePrefetchMessage]);

    const handlePrefetchFolder = useCallback((id: string) => {
        void qc.prefetchInfiniteQuery({
            queryKey: messageStreamKey(id, prefetchToken),
            queryFn: ({ pageParam }) => mailApi.listMessagesPage(id, pageParam, MESSAGE_PAGE_SIZE, prefetchToken),
            initialPageParam: 1,
            staleTime: 30_000
        });
    }, [qc, prefetchToken]);

    const handleDropMessage = useCallback((ids: string[], fromFolderId: string, toFolderId: string) => {
        if (fromFolderId === toFolderId) return;
        const all = expandIds(ids);
        for (const id of all) {
            messageAction.mutate({ type: "move", id, folderId: toFolderId });
        }
        if (all.length > 1) announce(`Moved ${all.length} messages`);
    }, [messageAction, announce, expandIds]);

    const handleContextAction = useCallback((
        action: "open" | "read" | "unread" | "flag" | "unflag" | "pin" | "unpin" | "archive" | "junk" | "trash",
        id: string
    ) => {
        if (action === "open") {
            setSelectedMessageId(id);
            return;
        }
        // In the Drafts folder the row IDs are draft IDs, not message IDs.
        // Route delete to the drafts API; other actions are no-ops on drafts.
        if (isDraftsFolder && action === "trash") {
            deleteDraft.mutate(id);
            setCompose(null);
            setSelectedMessageId(null);
            return;
        }
        // A conversation row acts on the whole conversation (Gmail).
        if (action === "archive" || action === "trash" || action === "junk") {
            moveWithUndo(expandIds([id]), action);
            return;
        }
        if (action === "read" || action === "unread") {
            setFlagsWithUndo(expandIds([id]), action);
            return;
        }
        messageAction.mutate({ type: action, id });
    }, [messageAction, isDraftsFolder, deleteDraft, moveWithUndo, setFlagsWithUndo, expandIds]);

    const handleBulkAction = useCallback((action: "read" | "unread" | "flag" | "unflag" | "archive" | "trash") => {
        const rowIds = Array.from(checkedIds);
        if (rowIds.length === 0) return;
        const ids = isDraftsFolder ? rowIds : expandIds(rowIds);
        // In the Drafts folder row IDs are draft IDs — route trash to the drafts API.
        if (isDraftsFolder && action === "trash") {
            for (const id of ids) deleteDraft.mutate(id);
            setCheckedIds(new Set());
            setCompose(null);
            setSelectedMessageId(null);
            announce(`Deleted ${ids.length} draft${ids.length === 1 ? "" : "s"}`);
            return;
        }
        setCheckedIds(new Set());
        if (action === "archive" || action === "trash") {
            moveWithUndo(ids, action);
            return;
        }
        // Stars go on the rows (the conversation's newest message), as Gmail does.
        setFlagsWithUndo(action === "flag" || action === "unflag" ? rowIds : ids, action);
    }, [checkedIds, isDraftsFolder, deleteDraft, announce, expandIds, moveWithUndo, setFlagsWithUndo]);

    const handleSelectMessage = (id: string) => {
        // Drafts list items carry the raw draft id. Opening one re-hydrates
        // compose rather than routing to ReadingPane — a draft has no
        // "read" view in this UI.
        if (isDraftsFolder) {
            const draft = drafts.data?.find((d) => d.id === id);
            if (draft) {
                setSelectedMessageId(id);
                setCompose({ seed: seedForDraft(draft), draftId: draft.id, key: "draft-" + draft.id });
                setLastDraftSavedAt(new Date(draft.updatedAt));
                setDraftSaveError(null);
                return;
            }
        }
        setCompose(null);
        setSelectedMessageId(id);
    };

    const shiftSelection = useCallback(
        (delta: 1 | -1) => {
            if (listMessages.length === 0) return;
            const idx = listMessages.findIndex((m) => m.id === activeRowId);
            const next = listMessages[Math.max(0, Math.min(listMessages.length - 1, (idx < 0 ? 0 : idx) + delta))];
            if (next) {
                setCompose(null);
                setSelectedMessageId(next.id);
            }
        },
        [listMessages, activeRowId]
    );

    /** The open message — or, in conversation view, every message of its conversation. */
    const currentIds = useCallback((): string[] => {
        if (!selectedMessageId) return [];
        return selectedConversation ? selectedConversation.messages.map((m) => m.id) : [selectedMessageId];
    }, [selectedMessageId, selectedConversation]);

    const resetDraftStatus = useCallback(() => {
        setLastDraftSavedAt(null);
        setDraftSaveError(null);
    }, []);

    const handleCompose = useCallback(() => {
        lastSavedDraftIdRef.current = null;
        setCompose({ seed: seedForNew(identities.data ?? []), draftId: null, key: crypto.randomUUID() });
        resetDraftStatus();
    }, [identities.data, resetDraftStatus]);

    // Tray-menu integration — Electron's tray fires `athavita:nav` with
    // a path; we map known paths back to MailPage state. No-op outside
    // the desktop shell, since `window.athavita` is undefined there.
    useEffect(() => {
        const bridge = typeof window !== "undefined" ? window.athavita : undefined;
        if (!bridge) return;
        return bridge.onNavigate((path) => {
            if (path === "/compose") {
                handleCompose();
            } else if (path === "/inbox" || path === "/") {
                setSelectedFolderId("inbox");
                setCompose(null);
            }
        });
    }, [handleCompose]);

    const handleReply = useCallback((message: Message) => {
        lastSavedDraftIdRef.current = null;
        setCompose({ seed: seedForReply(message, identities.data ?? [], false), draftId: null, key: crypto.randomUUID() });
        resetDraftStatus();
    }, [identities.data, resetDraftStatus]);

    // Per-message "AI reply": open a reply compose and auto-draft it with AI.
    const handleAiReply = useCallback((message: Message) => {
        lastSavedDraftIdRef.current = null;
        setCompose({ seed: seedForReply(message, identities.data ?? [], false), draftId: null, key: crypto.randomUUID(), autoAiReply: true });
        resetDraftStatus();
    }, [identities.data, resetDraftStatus]);

    const handleReplyAll = useCallback((message: Message) => {
        lastSavedDraftIdRef.current = null;
        setCompose({ seed: seedForReply(message, identities.data ?? [], true), draftId: null, key: crypto.randomUUID() });
        resetDraftStatus();
    }, [identities.data, resetDraftStatus]);

    const handleForward = useCallback((message: Message) => {
        lastSavedDraftIdRef.current = null;
        setCompose({ seed: seedForForward(message, identities.data ?? []), draftId: null, key: crypto.randomUUID() });
        resetDraftStatus();
    }, [identities.data, resetDraftStatus]);

    const handleCloseCompose = useCallback(() => {
        lastSavedDraftIdRef.current = null;
        setCompose(null);
        resetDraftStatus();
    }, [resetDraftStatus]);

    // Keyboard-shortcut handlers (`e` = archive, `Del` = trash) acting on the
    // currently selected/open message. Advance to the next row first so the
    // reading pane moves on Gmail-style, then apply the action.
    const handleArchive = useCallback(() => {
        if (!selectedMessageId || isDraftsFolder) return;
        const ids = currentIds();
        shiftSelection(1);
        moveWithUndo(ids, "archive");
    }, [selectedMessageId, isDraftsFolder, currentIds, shiftSelection, moveWithUndo]);
    const handleDelete = useCallback(() => {
        if (!selectedMessageId) return;
        const id = selectedMessageId;
        // Drafts carry draft IDs — route to the drafts API and close compose.
        if (isDraftsFolder) {
            shiftSelection(1);
            deleteDraft.mutate(id);
            setCompose(null);
            announce("Draft deleted");
            return;
        }
        const ids = currentIds();
        shiftSelection(1);
        moveWithUndo(ids, "trash");
    }, [selectedMessageId, isDraftsFolder, currentIds, shiftSelection, moveWithUndo, deleteDraft, announce]);
    const handleNext = useCallback(() => shiftSelection(1), [shiftSelection]);
    const handlePrev = useCallback(() => shiftSelection(-1), [shiftSelection]);

    // ── Command bar (Doc/MAIL_UI_REDESIGN.md) ────────────────────────────
    // Acts on the checked rows, else on the open message (its whole
    // conversation in conversation view).
    const labels = useLabels();
    const applyLabel = useApplyLabel();
    const snooze = useSnoozeMessage();
    const folderPaneOn = usePreferences((p) => p.layout.folderPane);
    const checkedCount = checkedIds.size;
    const targetRowIds = useMemo(
        () => (checkedCount > 0 ? Array.from(checkedIds) : activeRowId && !compose ? [activeRowId] : []),
        [checkedCount, checkedIds, activeRowId, compose]
    );
    const targetMessageIds = (): string[] =>
        checkedCount > 0 ? (isDraftsFolder ? Array.from(checkedIds) : expandIds(Array.from(checkedIds))) : currentIds();
    const targetRows = targetRowIds
        .map((id) => summaryById.get(id))
        .filter((m): m is MessageSummary => Boolean(m));
    const anyUnread = targetRows.some((m) => conversationByRow.get(m.id)?.unread ?? !m.isRead);
    const allFlagged = targetRows.length > 0 && targetRows.every((m) => m.isFlagged);
    const allPinned = targetRows.length > 0 && targetRows.every((m) => Boolean(m.isPinned));
    /** Checked rows clear; the open message moves on to the next one (Gmail). */
    const consumeTargets = () => {
        if (checkedCount > 0) setCheckedIds(new Set());
        else shiftSelection(1);
    };
    const commandActions: CommandBarActions = {
        onDelete: () => (checkedCount > 0 ? handleBulkAction("trash") : handleDelete()),
        onArchive: () => (checkedCount > 0 ? handleBulkAction("archive") : handleArchive()),
        onReportJunk: () => {
            const ids = targetMessageIds();
            consumeTargets();
            moveWithUndo(ids, "junk");
        },
        onMove: (folderId) => {
            const ids = targetMessageIds();
            if (ids.length === 0) return;
            consumeTargets();
            for (const id of ids) messageAction.mutate({ type: "move", id, folderId });
            const target = folders.data?.find((f) => f.id === folderId);
            announce(`Moved ${ids.length > 1 ? `${ids.length} messages ` : ""}to ${target ? displayFolderName(target) : "folder"}`);
        },
        onApplyLabel: (labelId) => {
            const ids = targetMessageIds();
            for (const id of ids) applyLabel.mutate({ messageId: id, labelId, apply: true });
            const label = labels.data?.find((l) => l.id === labelId);
            announce(`Label ${label?.name ?? ""} applied`);
        },
        onToggleRead: () => {
            const kind = anyUnread ? "read" : "unread";
            if (checkedCount > 0) handleBulkAction(kind);
            else setFlagsWithUndo(currentIds(), kind);
        },
        onToggleFlag: () => {
            const kind = allFlagged ? "unflag" : "flag";
            if (checkedCount > 0) handleBulkAction(kind);
            else setFlagsWithUndo(targetRowIds, kind);
        },
        onTogglePin: () => {
            for (const id of targetRowIds) messageAction.mutate({ type: allPinned ? "unpin" : "pin", id });
            announce(allPinned ? "Unpinned" : "Pinned to top");
        },
        onSnooze: (when) => {
            const ids = targetMessageIds();
            for (const id of ids) snooze.mutate({ messageId: id, wakeAt: when.toISOString() });
            announce(`Snoozed until ${when.toLocaleString()}`);
        },
        onCreateTask: (key) => createTaskFrom(
            targetRows.map((m) => ({ id: m.id, subject: m.subject, fromName: m.from.name, fromEmail: m.from.email })),
            key
        ),
        onUndo: handleUndo
    };

    // ── Keyboard shortcuts (Gmail set) ───────────────────────────────────
    const [shortcutsEnabled] = useShortcutsEnabled();
    const [showShortcuts, setShowShortcuts] = useState(false);
    const focusReader = () =>
        requestAnimationFrame(() => document.querySelector<HTMLElement>("[data-reading-pane]")?.focus());
    const goToFolder = (kind: GoToFolder) => {
        const folder = folders.data?.find((f) => f.kind === kind);
        if (searchMode) setSearchQuery("");
        handleSelectFolder(folder?.id ?? kind);
        announce(`Opened ${folder?.name ?? kind}`);
    };
    useMailShortcuts({
        enabled: shortcutsEnabled,
        onCompose: handleCompose,
        onArchive: handleArchive,
        onDelete: handleDelete,
        onNextMessage: handleNext,
        onPrevMessage: handlePrev,
        onOpen: () => {
            if (!selectedMessageId && listMessages[0]) setSelectedMessageId(listMessages[0].id);
            focusReader();
        },
        onBack: () => {
            setCompose(null);
            const list = document.querySelector<HTMLElement>('[aria-label="Message list"]');
            (list?.querySelector<HTMLElement>('[aria-current="true"]') ?? list?.querySelector<HTMLElement>("li button[draggable]"))?.focus();
        },
        onToggleStar: () => {
            if (!selectedMessageId || isDraftsFolder) return;
            const flagged = messageDetail.data?.id === selectedMessageId
                ? messageDetail.data.isFlagged
                : summaryById.get(selectedMessageId)?.isFlagged ?? false;
            setFlagsWithUndo([selectedMessageId], flagged ? "unflag" : "flag");
        },
        onMarkRead: () => { if (!isDraftsFolder) setFlagsWithUndo(currentIds(), "read"); },
        onMarkUnread: () => { if (!isDraftsFolder) setFlagsWithUndo(currentIds(), "unread"); },
        onReply: () => { if (messageDetail.data && !isDraftsFolder) handleReply(messageDetail.data); },
        onReplyAll: () => { if (messageDetail.data && !isDraftsFolder) handleReplyAll(messageDetail.data); },
        onForward: () => { if (messageDetail.data && !isDraftsFolder) handleForward(messageDetail.data); },
        onToggleSelect: () => { if (activeRowId) handleToggleChecked(activeRowId); },
        onGoTo: goToFolder,
        onUndo: handleUndo,
        onShowShortcuts: () => setShowShortcuts(true),
        searchInputRef: context?.searchInputRef
    });

    // Commands from the Ctrl+K palette (lib/mailCommands) — the same actions the keys run.
    useMailCommands((command) => {
        switch (command.type) {
            case "compose":
                handleCompose();
                break;
            case "folder":
                if (searchMode) setSearchQuery("");
                handleSelectFolder(command.id);
                break;
            case "shortcuts":
                setShowShortcuts(true);
                break;
        }
    });

    // Broadcast the currently open message so the AI panel can use it as reply context.
    // Privacy: we only broadcast to window events — the same message the user already sees.
    useEffect(() => {
        if (!messageDetail.data) return;
        const msg = messageDetail.data;
        window.dispatchEvent(
            new CustomEvent("mail:message-focused", {
                detail: {
                    message: {
                        id: msg.id,
                        subject: msg.subject,
                        fromName: msg.from.name,
                        fromEmail: msg.from.email,
                        bodyText: stripHtml(msg.bodyHtml).slice(0, 3000)
                    }
                }
            })
        );
    }, [messageDetail.data]);

    // Let the AI panel open a pre-filled compose window
    useEffect(() => {
        const handler = (e: Event) => {
            const seed = (e as CustomEvent<{ seed: Partial<ComposeSeed> }>).detail.seed;
            const identity =
                identities.data?.find((i) => i.isDefault) ?? identities.data?.[0];
            setCompose({
                seed: {
                    kind: seed.kind ?? "New",
                    identityId: seed.identityId ?? identity?.id ?? null,
                    to: seed.to ?? "",
                    cc: seed.cc ?? "",
                    bcc: seed.bcc ?? "",
                    subject: seed.subject ?? "",
                    bodyHtml: seed.bodyHtml ?? "<p></p>",
                    inReplyToMessageId: seed.inReplyToMessageId ?? null,
                    attachmentIds: seed.attachmentIds ?? []
                },
                draftId: null,
                key: crypto.randomUUID()
            });
            resetDraftStatus();
        };
        window.addEventListener("ai:open-compose", handler as EventListener);
        return () => window.removeEventListener("ai:open-compose", handler as EventListener);
    }, [identities.data, resetDraftStatus]);

    // Gmail-style "Sent — Undo" toast: holds the actual POST /api/messages
    // for UNDO_WINDOW_MS so the user can take it back. Compose closes
    // immediately on click for the snappy feel; the underlying request
    // doesn't fire until the timer expires (or never, if Undo wins).
    const UNDO_WINDOW_MS = 4_000;
    const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [pendingSend, setPendingSend] = useState<{ deadline: number } | null>(null);
    const [sendError, setSendError] = useState<string | null>(null);

    // Drive files shared as links from compose: grant the To/Cc/Bcc
    // recipients access (source "mail") right before the message leaves, so
    // the link works the moment it lands. Never blocks the send — a failed
    // grant becomes a warning toast the sender can act on in Drive.
    const [shareWarning, setShareWarning] = useState<string | null>(null);
    const shareToken = useAccessToken();
    const grantDriveShares = async (payload: ComposePayload): Promise<void> => {
        if (payload.driveShares.length === 0) return;
        const emails = recipientEmails(payload.to, payload.cc, payload.bcc);
        if (emails.length === 0) return;
        const failed: string[] = [];
        await Promise.all(payload.driveShares.map(async (s) => {
            try {
                await driveApi.grant(s.itemId, emails, payload.driveShareRole, shareToken);
            } catch (err) {
                failed.push(`${s.name} (${err instanceof Error ? err.message : String(err)})`);
            }
        }));
        if (failed.length > 0) {
            const msg = `Recipients may not be able to open ${failed.join(", ")}.`;
            setShareWarning(msg);
            announce(`Drive sharing failed. ${msg}`);
        }
    };

    const handleSend = (payload: ComposePayload) => {
        // Prefer compose.draftId (set by React state); fall back to the ref
        // (set synchronously in onSuccess) in case the state flush hasn't
        // landed yet when Send is clicked immediately after auto-save.
        const draftIdToDelete = compose?.draftId ?? lastSavedDraftIdRef.current ?? null;
        lastSavedDraftIdRef.current = null;
        setCompose(null);

        // If the user smashes Send on a second message before the first
        // window expires, fire the previous one immediately and start a
        // fresh undo countdown for the new one.
        if (undoTimerRef.current) {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
        }

        const deadline = Date.now() + UNDO_WINDOW_MS;
        setPendingSend({ deadline });
        announce(`Sending in ${Math.round(UNDO_WINDOW_MS / 1000)} seconds`);

        undoTimerRef.current = setTimeout(() => {
            undoTimerRef.current = null;
            setPendingSend(null);
            // Grants first (never throws), then the actual send.
            void grantDriveShares(payload).then(() => send.mutate(
                {
                    to: payload.to,
                    cc: payload.cc,
                    // Bcc used to be left out here (and had no field on the
                    // server), so Bcc recipients never got the message.
                    bcc: payload.bcc,
                    subject: payload.subject,
                    bodyHtml: payload.bodyHtml,
                    attachmentIds: payload.attachmentIds,
                    requestReadReceipt: payload.requestReadReceipt
                },
                {
                    onSuccess: () => {
                        if (draftIdToDelete) {
                            deleteDraft.mutate(draftIdToDelete);
                        }
                        announce("Message sent");
                    },
                    onError: (err: unknown) => {
                        const msg = err instanceof Error ? err.message : String(err);
                        announce(`Send failed: ${msg}`);
                        setSendError(msg);
                    }
                }
            ));
        }, UNDO_WINDOW_MS);
    };

    const handleUndoSend = () => {
        if (undoTimerRef.current) {
            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;
        }
        setPendingSend(null);
        announce("Send undone");
    };

    // Gmail "Send & Archive" — only available when the open compose
    // is a reply / reply-all (carries inReplyToMessageId). Sends the
    // reply via the same Undo-window path as a normal Send, and
    // optimistically fires an archive on the original message right
    // away. If Undo wins, the original stays archived (matches Gmail's
    // behaviour: archive is independent of the new outbound).
    const handleSendAndArchive = (payload: ComposePayload) => {
        if (payload.inReplyToMessageId) {
            messageAction.mutate({ type: "archive", id: payload.inReplyToMessageId });
        }
        handleSend(payload);
    };

    const handleSaveDraft = (payload: ComposePayload) => {
        saveDraft.mutate(
            {
                id: compose?.draftId ?? null,
                payload: {
                    identityId: payload.identityId,
                    inReplyToMessageId: payload.inReplyToMessageId,
                    kind: payload.kind,
                    to: payload.to,
                    cc: payload.cc,
                    bcc: payload.bcc,
                    subject: payload.subject,
                    bodyHtml: payload.bodyHtml,
                    attachmentIds: payload.attachmentIds
                }
            },
            {
                onSuccess: (saved) => {
                    lastSavedDraftIdRef.current = saved.id;
                    setCompose((prev) => (prev ? { ...prev, draftId: saved.id } : prev));
                    setLastDraftSavedAt(new Date());
                    setDraftSaveError(null);
                    announce("Draft saved");
                },
                onError: (err) => {
                    const msg = err instanceof Error ? err.message : String(err);
                    setDraftSaveError(msg);
                    announce(`Draft save failed: ${msg}`);
                }
            }
        );
    };

    const handleSchedule = (payload: ComposePayload, sendAt: Date) => {
        scheduleSend.mutate(
            {
                to: payload.to,
                cc: payload.cc,
                bcc: payload.bcc,
                subject: payload.subject,
                bodyHtml: payload.bodyHtml,
                attachmentIds: payload.attachmentIds,
                sendAt: sendAt.toISOString()
            },
            {
                onSuccess: () => {
                    const draftId = compose?.draftId ?? lastSavedDraftIdRef.current ?? null;
                    lastSavedDraftIdRef.current = null;
                    if (draftId) deleteDraft.mutate(draftId);
                    setCompose(null);
                    announce(`Scheduled for ${sendAt.toLocaleString()}`);
                    // Scheduled mail is sent server-side later; grant now so
                    // the links already work when it goes out.
                    void grantDriveShares(payload);
                }
            }
        );
    };

    return (
        // The approved mock-up: folder pane | (command bar over list + reader
        // cards with an 8 px gap on the grey canvas).
        <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-[var(--mail-canvas)]">
            {folderPaneOn ? (
                <FolderPane
                    selectedFolderId={shownFolderId}
                    onSelectFolder={handleSelectFolder}
                    onCompose={handleCompose}
                    onPrefetchFolder={handlePrefetchFolder}
                    onDropMessage={handleDropMessage}
                />
            ) : null}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <MailCommandBar
                {...commandActions}
                hasTarget={targetRowIds.length > 0}
                checkedCount={checkedCount}
                isDrafts={isDraftsFolder}
                anyUnread={anyUnread}
                allFlagged={allFlagged}
                allPinned={allPinned}
                canUndo={Boolean(undoEntry)}
                folders={folders.data ?? []}
                currentFolderId={shownFolderId}
                labels={labels.data ?? []}
                taskTargets={taskTargets}
                tabsAvailable={!searchMode && currentFolder?.kind === "inbox"}
            />
            {/* List + reader share this box, so "bottom" stacks only these two. */}
            <div className={cn(
                "flex min-h-0 min-w-0 flex-1 overflow-hidden p-2",
                readingPane === "bottom" ? "flex-col gap-2" : "flex-row",
                // With the pane on the right the resize handle is the gutter.
                readingPane === "off" && "gap-2"
            )}>
            <ResizableListColumn
                enabled={readingPane === "right"}
                className={cn(
                "flex min-w-0 flex-col overflow-hidden rounded-lg bg-card shadow-[var(--mail-shadow-4)]",
                readingPane === "bottom"
                    // Stacked: list on top, reader underneath.
                    ? "h-[44%] w-full shrink-0"
                    : readingPane === "off"
                        // Hidden: the list owns the width and there is no reader,
                        // unless a message is open — then it takes over.
                        ? cn("h-full w-full flex-1", readerVisible && "hidden")
                        : undefined
            )}>
                <MessageList
                    // A new folder or search starts at the top with fresh scroll state.
                    key={searchMode ? `search:${searchQuery}` : selectedFolderId}
                    messages={listMessages}
                    allMessages={activeMessages}
                    selectedMessageId={activeRowId}
                    onSelectMessage={handleSelectMessage}
                    folderName={folderName}
                    isLoading={activePending}
                    isError={activeError}
                    errorMessage={activeErrorMessage}
                    // Refreshing the message list also refetches folders, so unread badges move in
                    // step with the list rather than lagging until the next poll.
                    onRefresh={() => {
                        if (searchMode) void search.refetch();
                        else void messages.refetch();
                        void folders.refetch();
                    }}
                    isRefreshing={(searchMode ? search.isFetching : messages.isFetching) || folders.isFetching}
                    conversations={conversations ? conversationByRow : undefined}
                    conversationView={conversationView}
                    onToggleConversationView={isDraftsFolder ? undefined : () => {
                        setConversationView(!conversationView);
                        setCheckedIds(new Set());
                        announce(conversationView ? "Conversation view off" : "Conversation view on");
                    }}
                    banner={searchMode ? <SearchChips query={searchQuery} onChange={setSearchQuery} /> : undefined}
                    emptyText={searchMode
                        ? "No messages match your search."
                        : tabsOn
                            ? (inboxTab === "direct"
                                ? "Nothing from people in the mail loaded so far — the rest is in Updates."
                                : "No updates in the mail loaded so far.")
                            : undefined}
                    tabs={tabsOn ? {
                        value: inboxTab,
                        counts: tabUnread,
                        onChange: (t) => { setInboxTab(t); setCheckedIds(new Set()); }
                    } : undefined}
                    checkedIds={checkedIds}
                    onToggleChecked={handleToggleChecked}
                    onToggleCheckedAll={handleToggleCheckedAll}
                    onContextAction={handleContextAction}
                    hoverActionsEnabled={!isDraftsFolder}
                    taskTargets={taskTargets}
                    onCreateTask={isDraftsFolder ? undefined : (m, key) =>
                        createTaskFrom([{ id: m.id, subject: m.subject, fromName: m.from.name, fromEmail: m.from.email }], key)}
                    onPrefetchMessage={handlePrefetchMessage}
                    highlightTerms={searchMode ? highlightTerms(searchQuery) : undefined}
                    filter={filters}
                    onFilterChange={setFilters}
                    pager={searchMode
                        ? (search.data ? {
                            page: searchPage,
                            pageSize: SEARCH_PAGE_SIZE,
                            total: search.data.totalHits,
                            onPrev: () => { setSearchPage((p) => Math.max(1, p - 1)); setCheckedIds(new Set()); },
                            onNext: () => { setSearchPage((p) => p + 1); setCheckedIds(new Set()); }
                        } : undefined)
                        : undefined}
                    // Folders scroll-load: the next batch comes as the end of the list nears.
                    stream={!searchMode && !isDraftsFolder && messages.data ? {
                        total: streamTotal,
                        hasMore: Boolean(messages.hasNextPage),
                        loading: messages.isFetchingNextPage,
                        failed: messages.isFetchNextPageError,
                        onLoadMore: () => void messages.fetchNextPage()
                    } : undefined}
                    unreadTotal={!searchMode && !isDraftsFolder
                        ? currentFolder?.unreadCount
                        : undefined}
                />
            </ResizableListColumn>
            {compose ? (
                <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg bg-card shadow-[var(--mail-shadow-4)]">
                <Suspense
                    fallback={
                        <section className="flex h-full flex-1 items-center justify-center text-sm text-muted-foreground">
                            Loading composer…
                        </section>
                    }
                >
                    <ComposePane
                        key={compose.key}
                        seed={compose.seed}
                        autoAiReply={compose.autoAiReply}
                        onSend={handleSend}
                        onSaveDraft={handleSaveDraft}
                        onSchedule={handleSchedule}
                        onSendAndArchive={handleSendAndArchive}
                        onClose={handleCloseCompose}
                        onDeleteDraft={compose.draftId ? () => {
                            const id = compose.draftId ?? lastSavedDraftIdRef.current;
                            if (id) deleteDraft.mutate(id);
                            lastSavedDraftIdRef.current = null;
                            setCompose(null);
                            setSelectedMessageId(null);
                        } : undefined}
                        sending={send.isPending}
                        draftSaving={saveDraft.isPending}
                        lastSavedAt={lastDraftSavedAt}
                        draftSaveError={draftSaveError}
                        scheduling={scheduleSend.isPending}
                    />
                </Suspense>
                </div>
            ) : isDraftsFolder ? (
                <section className="flex h-full flex-1 items-center justify-center rounded-lg bg-card text-sm text-muted-foreground shadow-[var(--mail-shadow-4)]">
                    {filteredMessages.length === 0
                        ? "No drafts yet — start composing to auto-save one here."
                        : "Select a draft to continue editing."}
                </section>
            ) : selectedConversation && selectedConversation.messages.length > 1 ? (
                <ConversationView
                    key={selectedConversation.key}
                    messages={selectedConversation.messages}
                    anchorId={selectedConversation.latest.id}
                    onReply={handleReply}
                    onReplyAll={handleReplyAll}
                    onForward={handleForward}
                />
            ) : (
                <ReadingPane
                    message={messageDetail.data ?? null}
                    isLoading={messageDetail.isPending && Boolean(selectedMessageId)}
                    preview={selectedMessageId ? summaryById.get(selectedMessageId) ?? null : null}
                    isError={messageDetail.isError}
                    errorMessage={messageDetail.error?.message}
                    onReply={handleReply}
                    onReplyAll={handleReplyAll}
                    onForward={handleForward}
                    onAiReply={handleAiReply}
                    onSelectMessage={setSelectedMessageId}
                />
            )}
            </div>
            </div>
            {newMailToast ? (
                <NewMailToast
                    count={newMailToast.count}
                    onView={() => {
                        setNewMailToast(null);
                        handleSelectFolder("inbox");
                    }}
                    onDismiss={() => setNewMailToast(null)}
                />
            ) : null}
            {pendingSend ? (
                <UndoSendToast deadline={pendingSend.deadline} onUndo={handleUndoSend} />
            ) : null}
            {sendError ? (
                <SendErrorToast message={sendError} onDismiss={() => setSendError(null)} />
            ) : null}
            {shareWarning && !sendError ? (
                <ShareWarningToast message={shareWarning} onDismiss={() => setShareWarning(null)} />
            ) : null}
            {undoEntry?.toast && !pendingSend ? (
                <ActionUndoToast
                    key={undoEntry.at}
                    label={undoEntry.label}
                    onUndo={handleUndo}
                    onDismiss={() => setUndoEntry((u) => (u ? { ...u, toast: false } : u))}
                />
            ) : null}
            <ShortcutsDialog open={showShortcuts} onOpenChange={setShowShortcuts} enabled={shortcutsEnabled} />
        </div>
    );
}

/** Gmail's "Conversation archived. Undo" — bottom-left, auto-hides; z works too. */
function ActionUndoToast({ label, onUndo, onDismiss }: { label: string; onUndo: () => void; onDismiss: () => void }) {
    useEffect(() => {
        const id = setTimeout(onDismiss, 8000);
        return () => clearTimeout(id);
    }, [onDismiss]);
    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto fixed bottom-6 left-6 z-50 flex max-w-[calc(100vw-3rem)] items-center gap-3 rounded-md bg-foreground px-4 py-3 text-sm text-background shadow-lg"
        >
            <span className="min-w-0 truncate">{label}</span>
            <button
                type="button"
                onClick={onUndo}
                className="shrink-0 font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
            >
                Undo
            </button>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="shrink-0 opacity-70 hover:opacity-100 focus:outline-none focus-visible:opacity-100"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}

/** Gmail-style new-mail arrival toast — bottom-left, auto-dismisses. */
function NewMailToast({
    count,
    onView,
    onDismiss
}: {
    count: number;
    onView: () => void;
    onDismiss: () => void;
}) {
    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto fixed bottom-20 left-6 z-50 flex items-center gap-3 rounded-md bg-[#0078D4] px-4 py-3 text-sm text-white shadow-lg"
        >
            <Mail className="h-4 w-4 shrink-0" aria-hidden />
            <span>{count === 1 ? "New message arrived" : `${count} new messages arrived`}</span>
            <button
                type="button"
                onClick={onView}
                className="font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
            >
                View
            </button>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="ml-1 opacity-70 hover:opacity-100 focus:outline-none"
            >
                ✕
            </button>
        </div>
    );
}

/**
 * Bottom-left toast that mirrors Gmail's "Sending… Undo" behaviour.
 * Renders a live countdown until <code>deadline</code>, then hides
 * itself (the parent unmounts it once the actual mutation fires).
 */
function UndoSendToast({ deadline, onUndo }: { deadline: number; onUndo: () => void }) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, []);
    const remainingMs = Math.max(0, deadline - now);
    const remainingSec = Math.ceil(remainingMs / 1000);
    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto fixed bottom-6 left-6 z-50 flex items-center gap-3 rounded-md bg-foreground px-4 py-3 text-sm text-background shadow-lg"
        >
            <span>
                Sending in {remainingSec}s
            </span>
            <button
                type="button"
                onClick={onUndo}
                className="font-semibold underline-offset-2 hover:underline focus:outline-none focus-visible:underline"
            >
                Undo
            </button>
        </div>
    );
}

function SendErrorToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    useEffect(() => {
        const id = setTimeout(onDismiss, 8000);
        return () => clearTimeout(id);
    }, [onDismiss]);
    return (
        <div
            role="alert"
            aria-live="assertive"
            className="pointer-events-auto fixed bottom-6 left-6 z-50 flex max-w-sm items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-lg dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
            <span className="flex-1">
                <span className="font-semibold">Send failed</span> — message is still in Drafts.
                <span className="block mt-0.5 text-xs opacity-75">{message}</span>
            </span>
            <button type="button" onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100">✕</button>
        </div>
    );
}

/** Amber sibling of SendErrorToast: the mail went out, a Drive grant didn't. */
function ShareWarningToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    useEffect(() => {
        const id = setTimeout(onDismiss, 12000);
        return () => clearTimeout(id);
    }, [onDismiss]);
    return (
        <div
            role="alert"
            className="pointer-events-auto fixed bottom-6 left-6 z-50 flex max-w-sm items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-lg dark:border-amber-700/60 dark:bg-amber-950 dark:text-amber-100"
        >
            <span className="flex-1">
                <span className="font-semibold">Drive sharing failed</span> — share the files from Drive.
                <span className="mt-0.5 block text-xs opacity-80">{message}</span>
            </span>
            <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 opacity-60 hover:opacity-100">✕</button>
        </div>
    );
}

/** Bare, de-duplicated addresses from To / Cc / Bcc ("Name <a@b>" or "a@b"). */
function recipientEmails(...fields: string[]): string[] {
    const out = new Set<string>();
    for (const raw of fields) {
        for (const part of raw.split(/[,;]/)) {
            const trimmed = part.trim();
            if (!trimmed) continue;
            const angle = trimmed.match(/<\s*([^>\s]+)\s*>/);
            const candidate = (angle?.[1] ?? trimmed).trim().toLowerCase();
            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) out.add(candidate);
        }
    }
    return Array.from(out);
}

// Shape a saved draft as a MessageSummary so the existing MessageList can
// render it. We use the draft's own id as the row id — handleSelectMessage
// detects we're in the Drafts folder and routes the click to Compose.
function draftToSummary(d: DraftDto): MessageSummary {
    const recipients = d.to.trim();
    const previewText = stripHtml(d.bodyHtml).slice(0, 120);
    return {
        id: d.id,
        folderId: "drafts",
        from: { name: recipients ? `To: ${recipients}` : "Draft", email: recipients },
        subject: d.subject.trim() || "(no subject)",
        preview: previewText,
        receivedAt: d.updatedAt,
        isRead: true,
        isFlagged: false,
        hasAttachments: d.attachmentIds.length > 0,
        category: null
    };
}

function stripHtml(html: string): string {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
